// Project state simulator engine - mirrors Project#check_state / #check_packing_state / #start_packaging_task
// and ProjectTask#refresh_project_state from cp_production_managment (master, 2026-10-30).

export type ProjectState =
  | 'unconfirmed' | 'confirmed' | 'waiting' | 'in_production'
  | 'in_packing' | 'ready_for_delivery' | 'delivered' | 'canceled';

export type TaskStatus = 'todo' | 'in_progress' | 'done' | 'waiting';

export interface Task {
  id: number;
  name: string;
  machine: string;
  typeId: number;
  status: TaskStatus;
}

export interface Project {
  name: string;
  state: ProjectState;
  packFinished: boolean;
  tasks: Task[];
}

export type LineKind = 'rule' | 'skip' | 'effect' | 'cp' | 'warn' | 'cascade';

export interface LogLine {
  kind: LineKind;
  text: string;
  ref?: string;
}

export interface LogEntry {
  step: number;
  trigger: string;
  where: string;
  before: ProjectState;
  after: ProjectState;
  lines: LogLine[];
}

export const TT_PACKAGING = 12;

export const STATE_LABEL: Record<ProjectState, string> = {
  unconfirmed: 'Unconfirmed',
  confirmed: 'Confirmed',
  waiting: 'Waiting for production',
  in_production: 'Production',
  in_packing: 'In packing',
  ready_for_delivery: 'Ready For Delivery',
  delivered: 'Delivered',
  canceled: 'Canceled',
};

export const TRACK: ProjectState[] = ['confirmed', 'waiting', 'in_production', 'in_packing', 'ready_for_delivery', 'delivered'];
export const ALL_STATES: ProjectState[] = ['unconfirmed', ...TRACK, 'canceled'];

export const STATUS_LABEL: Record<TaskStatus, string> = {
  todo: 'TODO',
  waiting: 'WAITING',
  in_progress: 'IN PROGRESS',
  done: 'DONE',
};
export const STATUSES: TaskStatus[] = ['todo', 'waiting', 'in_progress', 'done'];

function initialProject(): Project {
  return {
    name: '44 Linus 4x4-Premium',
    state: 'in_production',
    packFinished: false,
    tasks: [
      { id: 101, name: 'Wall logs', machine: 'HM-123-JL', typeId: 2, status: 'in_progress' },
      { id: 102, name: 'Beams, Columns, Purlins', machine: 'Hundegger-RD', typeId: 3, status: 'todo' },
      { id: 103, name: 'Floor, Roof, Terrace boards', machine: 'Opticut', typeId: 4, status: 'todo' },
    ],
  };
}

export const nonPackaging = (p: Project) => p.tasks.filter((t) => t.typeId !== TT_PACKAGING);
export const packagingTask = (p: Project) => p.tasks.find((t) => t.typeId === TT_PACKAGING);
export const tasksStarted = (p: Project) => nonPackaging(p).filter((t) => t.status === 'in_progress' || t.status === 'done');

// Project#progress_bar[2] - integer percent of non-packaging tasks done, 0 when there are none
export function progress(p: Project): number {
  const all = nonPackaging(p);
  const done = all.filter((t) => t.status === 'done').length;
  return Math.floor((done / (all.length === 0 ? 1 : all.length)) * 100);
}
export const completed = (p: Project) => progress(p) === 100;

export class Simulator {
  project: Project = initialProject();
  log: LogEntry[] = [];
  private step = 0;
  private nextTaskId = 900;
  private lines: LogLine[] = [];

  reset(): void {
    this.project = initialProject();
    this.log = [];
    this.step = 0;
    this.nextTaskId = 900;
  }

  // what Project#save would produce right now, without changing anything
  previewSave(): ProjectState {
    const copy: Project = JSON.parse(JSON.stringify(this.project));
    const saved = this.lines;
    this.lines = [];
    const real = this.project;
    this.project = copy;
    this.checkState(false);
    const result = this.project.state;
    this.project = real;
    this.lines = saved;
    return result;
  }

  // ------------------------------------------------ user actions (each = one log entry)

  changeTask(taskId: number, status: TaskStatus, recalc: boolean): void {
    const task = this.project.tasks.find((t) => t.id === taskId);
    if (!task || task.status === status) return;
    const isPackaging = task.typeId === TT_PACKAGING;

    this.run(
      `${task.name}: ${STATUS_LABEL[task.status]} → ${STATUS_LABEL[status]}`,
      recalc ? 'Tasks list – mass status change' : 'Task Management / status badge',
      () => {
        this.updateTaskStatus(task, status);
        if (recalc) {
          this.line('cascade', 'The mass action then saves the project (p.save).', 'project_tasks_controller.rb#update');
          this.saveProject();
        } else if (!isPackaging) {
          this.line('warn', 'The project is NOT saved, so its state is not recalculated. It changes only on the next project save.', 'task_management_controller.rb#update');
        }
      }
    );
  }

  saveProjectAction(): void {
    this.run('Project saved', 'Project edit / CabinPlanner sync / any action that saves the project', () => this.saveProject());
  }

  setStateManually(state: ProjectState): void {
    if (state === this.project.state) return;
    this.run(`State set manually to ${STATE_LABEL[state]}`, 'Project form / mass action on projects', () =>
      this.saveProject(state)
    );
  }

  addPackingItem(): void {
    this.run('Packing item added', 'Packing – new package', () => {
      let packaging = packagingTask(this.project);
      if (!packaging) {
        packaging = this.createPackagingTask('packing_items_controller.rb – create_right_task');
      }
      this.updateTaskStatus(packaging, 'in_progress', 'packing_items_controller.rb – update(status: :in_progress)');
    });
  }

  finishPacking(): void {
    if (this.project.packFinished) return;
    this.run('Packing finished (pack_finished)', 'Packing – “Packing is finished”', () => {
      this.project.packFinished = true;
      this.line('effect', 'pack_finished = true, project saved.', 'projects_controller.rb#change_packing_state');
      this.saveProject();
      this.line('effect', 'Activity: “Packing is finished”');
      const packaging = packagingTask(this.project);
      if (packaging) {
        this.updateTaskStatus(packaging, 'done', 'ProjectTask#finish_packing!');
      } else {
        this.line('skip', 'No Packaging task – finish_packing! has nothing to close.', 'ProjectTask#finish_packing!');
      }
    });
  }

  undoFinishPacking(): void {
    if (!this.project.packFinished) return;
    this.run('Packing reopened', 'Packing – “Packing is enabled back”', () => {
      this.project.packFinished = false;
      this.line('effect', 'pack_finished = false, project saved.', 'projects_controller.rb#change_packing_state');
      this.saveProject();
      this.line('effect', 'Activity: “Packing is enabled back”');
      const packaging = packagingTask(this.project);
      if (packaging) this.updateTaskStatus(packaging, 'in_progress', 'ProjectTask#undo_finish_packing!');
    });
  }

  dispatchTruck(): void {
    if (this.project.state === 'delivered') return;
    this.run('Truck dispatched', 'Truck#check_projects_state', () => this.saveProject('delivered'));
  }

  // ------------------------------------------------ model callbacks

  // ProjectTask#update(status:) + after_update :refresh_project_state (packaging only)
  private updateTaskStatus(task: Task, status: TaskStatus, ref?: string): void {
    const from = task.status;
    if (from === status) return;
    task.status = status;
    this.line('effect', `Task ${task.name}: ${STATUS_LABEL[from]} → ${STATUS_LABEL[status]}`, ref);
    if (task.typeId === TT_PACKAGING) {
      this.line('cascade', 'Packaging task status changed → project saved (refresh_project_state).', 'project_task.rb#refresh_project_state');
      this.saveProject();
    }
  }

  private createPackagingTask(ref: string): Task {
    const t: Task = { id: this.nextTaskId++, name: 'Packaging', machine: '—', typeId: TT_PACKAGING, status: 'todo' };
    this.project.tasks.push(t);
    this.line('effect', 'Packaging task created (TODO).', ref);
    return t;
  }

  // Project#save - before_update callbacks + after_update :start_packaging_task
  private saveProject(manualState?: ProjectState): void {
    const p = this.project;
    const before = p.state;
    if (manualState) p.state = manualState;
    const stateSetManually = p.state !== before;

    this.checkState(stateSetManually);

    if (p.state !== before) {
      this.line('effect', `Activity: “Project state changed to ${STATE_LABEL[p.state]}”`, 'Project#create_state_activity');
      if (p.state === 'in_packing') {
        this.line('cp', 'NOT sent to CabinPlanner – in_packing is an internal PM state.', 'Project#send_state_msg');
      } else {
        this.line('cp', `BrokerLog → CabinPlanner: PROJECT_STATUS_CHANGE (${p.state})`, 'Project#send_state_msg');
      }
      // after_update :start_packaging_task, if: @state_set_manually && saved_change_to_state? && in_packing?
      if (stateSetManually && p.state === 'in_packing') {
        const packaging = packagingTask(p);
        if (packaging && (packaging.status === 'todo' || packaging.status === 'waiting')) {
          this.line('rule', 'State set manually to in_packing → the Packaging task starts.', 'Project#start_packaging_task');
          this.updateTaskStatus(packaging, 'in_progress');
        } else {
          this.line('skip', packaging ? 'Packaging already started – start_packaging_task does nothing.' : 'No Packaging task – start_packaging_task does nothing.', 'Project#start_packaging_task');
        }
      }
    } else {
      this.line('skip', 'Project state unchanged.');
    }
  }

  // Project#check_state (before_update)
  private checkState(stateSetManually: boolean): void {
    const p = this.project;
    if (stateSetManually) {
      this.line('rule', `State set manually (${STATE_LABEL[p.state]}) – packing rules are skipped.`, 'Project#check_state – @state_set_manually');
    }

    const started = tasksStarted(p).length;
    if ((p.state === 'confirmed' || p.state === 'waiting') && started > 0) {
      this.line('rule', `${STATE_LABEL[p.state]} and ${started} task(s) IN PROGRESS/DONE → Production.`, 'Project#check_state');
      p.state = 'in_production';
    }

    if (p.state === 'in_production') {
      const pct = progress(p);
      if (pct === 100) {
        this.line('rule', 'Production and all tasks (except Packaging) DONE → In packing.', 'Project#check_state');
        p.state = 'in_packing';
        if (!packagingTask(p)) this.createPackagingTask('Project#check_state');
      } else {
        this.line('skip', `Production, but progress is ${pct}% – waiting for all tasks to be DONE.`, 'Project#check_state');
      }
    }

    if (!stateSetManually) this.checkPackingState();
  }

  // Project#check_packing_state
  private checkPackingState(): void {
    const p = this.project;
    const packaging = packagingTask(p);
    if (!packaging) return;

    const startedPackaging = packaging.status === 'waiting' || packaging.status === 'in_progress';
    if (['confirmed', 'waiting', 'in_production', 'ready_for_delivery'].includes(p.state) && startedPackaging) {
      this.line('rule', `Packaging ${STATUS_LABEL[packaging.status]} → In packing.`, 'Project#check_packing_state');
      p.state = 'in_packing';
    } else if (p.state === 'in_packing' && packaging.status === 'done') {
      if (p.packFinished) {
        this.line('rule', 'In packing, Packaging DONE and packing finished → Ready For Delivery.', 'Project#check_packing_state');
        p.state = 'ready_for_delivery';
      } else {
        this.line('skip', 'Packaging DONE, but “Finish packing” not pressed yet – stays In packing.', 'Project#check_packing_state');
      }
    }
  }

  // ------------------------------------------------ log plumbing

  private line(kind: LineKind, text: string, ref?: string): void {
    this.lines.push({ kind, text, ref });
  }

  private run(trigger: string, where: string, body: () => void): void {
    const before = this.project.state;
    this.lines = [];
    body();
    this.log.unshift({ step: ++this.step, trigger, where, before, after: this.project.state, lines: this.lines });
    this.lines = [];
  }
}
